import { FC, useEffect, useRef } from "react";
import { useController, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { loadScript } from "@/utils/loadScript";
import { FormItem, FormLabel } from "@/components/ui/form";
import { cn } from "@/lib/utils";

interface Props {
  control: Control<any>;
  disabled?: boolean;
  environment: string;
  merchantId: string;
  merchantSiteId: string;
  onInit: (ctx: {
    safeCharge: any;
    scFields: { cardNumber: any; cardExpiry: any; cardCvc: any };
  }) => void;
}

const NuveiFormFields: FC<Props> = (props) => {
  const { control, disabled, environment, merchantId, merchantSiteId, onInit } =
    props;
  const { t, i18n } = useTranslation();

  const ccNumberRef = useRef<HTMLDivElement>(null);
  const ccExpiryRef = useRef<HTMLDivElement>(null);
  const ccCvcRef = useRef<HTMLDivElement>(null);

  const {
    field: numberField,
    fieldState: { error: numberError },
  } = useController({ control, name: "card_number", defaultValue: false });
  const {
    field: expiryField,
    fieldState: { error: expiryError },
  } = useController({ control, name: "card_expiry", defaultValue: false });
  const {
    field: cvcField,
    fieldState: { error: cvcError },
  } = useController({ control, name: "card_cvc", defaultValue: false });

  useEffect(() => {
    if (disabled) return;

    let cancelled = false;

    (async () => {
      try {
        await loadScript(
          "https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js",
        );

        if (cancelled) return;

        const sfc = (window as any).SafeCharge({
          merchantId,
          merchantSiteId,
          env: environment,
        });

        const fields = sfc.fields({
          locale: i18n.language,
          fonts: [{ cssUrl: "https://fonts.googleapis.com/css?family=Roboto" }],
        });

        if (ccNumberRef.current) ccNumberRef.current.innerHTML = "";
        if (ccExpiryRef.current) ccExpiryRef.current.innerHTML = "";
        if (ccCvcRef.current) ccCvcRef.current.innerHTML = "";

        const cardNumber = fields.create("ccNumber", {});
        cardNumber.attach(ccNumberRef.current);
        cardNumber.on("change", (evt: any) =>
          numberField.onChange(!!evt.complete),
        );

        const cardExpiry = fields.create("ccExpiration", {});
        cardExpiry.attach(ccExpiryRef.current);
        cardExpiry.on("change", (evt: any) =>
          expiryField.onChange(!!evt.complete),
        );

        const cardCvc = fields.create("ccCvc", {});
        cardCvc.attach(ccCvcRef.current);
        cardCvc.on("change", (evt: any) => cvcField.onChange(!!evt.complete));

        onInit({
          safeCharge: sfc,
          scFields: { cardNumber, cardExpiry, cardCvc },
        });
      } catch {
        // init failed silently
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (ccNumberRef.current) ccNumberRef.current.innerHTML = "";
        if (ccExpiryRef.current) ccExpiryRef.current.innerHTML = "";
        if (ccCvcRef.current) ccCvcRef.current.innerHTML = "";
      } catch {}
    };
  }, [disabled, merchantId, merchantSiteId, environment, i18n.language]);

  if (disabled) return null;

  return (
    <>
      <FormItem>
        <FormLabel>
          {t("models.paymentInfo.cardNumber", "Card number")}
          <span className="text-red-500 ml-1">*</span>
        </FormLabel>
        <div
          ref={ccNumberRef}
          className={cn(
            "h-9 rounded-md border px-3 py-2",
            numberError ? "border-destructive" : "border-input",
          )}
        />
        {numberError && (
          <p className="text-xs font-medium text-destructive">
            {t("validation.required", "{{label}} is required", {
              label: t("models.paymentInfo.cardNumber", "Card number"),
            })}
          </p>
        )}
      </FormItem>

      <div className="grid md:grid-cols-2 gap-4">
        <FormItem>
          <FormLabel>
            {t("models.paymentInfo.dateExpiration", "Expiry date")}
            <span className="text-red-500 ml-1">*</span>
          </FormLabel>
          <div
            ref={ccExpiryRef}
            className={cn(
              "h-9 rounded-md border px-3 py-2",
              expiryError ? "border-destructive" : "border-input",
            )}
          />
          {expiryError && (
            <p className="text-xs font-medium text-destructive">
              {t("validation.required", "{{label}} is required", {
                label: t("models.paymentInfo.dateExpiration", "Expiry date"),
              })}
            </p>
          )}
        </FormItem>

        <FormItem>
          <FormLabel>
            {t("models.paymentInfo.cvc", "CVC")}
            <span className="text-red-500 ml-1">*</span>
          </FormLabel>
          <div
            ref={ccCvcRef}
            className={cn(
              "h-9 rounded-md border px-3 py-2",
              cvcError ? "border-destructive" : "border-input",
            )}
          />
          {cvcError && (
            <p className="text-xs font-medium text-destructive">
              {t("validation.required", "{{label}} is required", {
                label: t("models.paymentInfo.cvc", "CVC"),
              })}
            </p>
          )}
        </FormItem>
      </div>
    </>
  );
};

export default NuveiFormFields;
